/**
 * One-time viewport animation coordinator.
 *
 * Sections are observed independently, while their entrance timelines share a
 * small queue. Only one non-pinned reveal runs at a time, preventing multiple
 * sections from competing for the compositor during a fast scroll.
 */

const activeEntries = new Set();
const animationQueue = [];
let activeAnimation = null;
let globalFrame = 0;
let globalListenersActive = false;
let lastScrollY = 0;
let lastScrollTime = 0;
let currentVelocity = 0;

const refreshGeometry = (entry) => {
  if (!entry?.target?.isConnected) return;
  const rect = entry.target.getBoundingClientRect();
  const scrollY = window.scrollY || 0;
  entry.top = rect.top + scrollY;
  entry.bottom = entry.top + Math.max(1, rect.height);
};

const refreshAllGeometry = () => {
  activeEntries.forEach(refreshGeometry);
  scheduleGlobalCheck();
};

const runGlobalCheck = () => {
  globalFrame = 0;
  const scrollY = window.scrollY || 0;
  const viewportHeight =
    window.innerHeight || document.documentElement.clientHeight || 1;
  const viewportBottom = scrollY + viewportHeight;

  activeEntries.forEach((entry) => {
    entry.checkCached(scrollY, viewportBottom, currentVelocity);
  });
};

function scheduleGlobalCheck() {
  if (globalFrame || !activeEntries.size) return;
  globalFrame = window.requestAnimationFrame(runGlobalCheck);
}

const onGlobalScroll = () => {
  const now = performance.now();
  const nextY = window.scrollY || 0;
  const elapsed = Math.max(16, now - lastScrollTime);

  currentVelocity = (Math.abs(nextY - lastScrollY) / elapsed) * 1000;
  lastScrollY = nextY;
  lastScrollTime = now;
  scheduleGlobalCheck();
};

const activateGlobalListeners = () => {
  if (globalListenersActive) return;
  globalListenersActive = true;
  lastScrollY = window.scrollY || 0;
  lastScrollTime = performance.now();
  window.addEventListener("scroll", onGlobalScroll, { passive: true });
  window.addEventListener("resize", refreshAllGeometry, { passive: true });
};

const deactivateGlobalListeners = () => {
  if (!globalListenersActive || activeEntries.size) return;
  globalListenersActive = false;
  window.removeEventListener("scroll", onGlobalScroll);
  window.removeEventListener("resize", refreshAllGeometry);

  if (globalFrame) {
    window.cancelAnimationFrame(globalFrame);
    globalFrame = 0;
  }
};

const registerEntry = (entry) => {
  refreshGeometry(entry);
  activeEntries.add(entry);
  activateGlobalListeners();
  scheduleGlobalCheck();
};

const unregisterEntry = (entry) => {
  activeEntries.delete(entry);
  deactivateGlobalListeners();
};

const runNextAnimation = () => {
  if (activeAnimation || !animationQueue.length) return;

  const job = animationQueue.shift();
  if (!job || job.cancelled) {
    runNextAnimation();
    return;
  }

  activeAnimation = job;
  let released = false;

  const releaseQueue = () => {
    if (released) return;
    released = true;
    if (activeAnimation === job) activeAnimation = null;
    job.release = null;
    window.requestAnimationFrame(runNextAnimation);
  };

  job.release = releaseQueue;

  try {
    job.run(releaseQueue);
  } catch (error) {
    releaseQueue();
    throw error;
  }
};

const enqueueAnimation = (job) => {
  animationQueue.push(job);
  runNextAnimation();
};

const cancelAnimationJob = (job) => {
  if (!job) return;
  job.cancelled = true;

  const index = animationQueue.indexOf(job);
  if (index >= 0) animationQueue.splice(index, 1);

  job.release?.();
};

export function createOneTimeViewportAnimation({
  target,
  play,
  finish,
  rootMargin = "0px 0px -10% 0px",
  threshold = 0.01,
  preload = 180,
  fastVelocity = 1100,
}) {
  if (!target) {
    return {
      destroy() {},
      complete() {},
    };
  }

  let observer = null;
  let resizeObserver = null;
  let started = false;
  let completed = false;
  let queuedJob = null;

  const entry = {
    target,
    top: 0,
    bottom: 1,
    checkCached(scrollY, viewportBottom, velocity) {
      if (completed) return;

      if (entry.bottom <= scrollY) {
        forceComplete();
        return;
      }

      if (!started && entry.top <= viewportBottom + preload) {
        start(velocity);
      }
    },
  };

  const disconnect = () => {
    observer?.disconnect();
    observer = null;
    resizeObserver?.disconnect();
    resizeObserver = null;
    unregisterEntry(entry);
  };

  const markComplete = () => {
    if (completed) return;
    completed = true;
    disconnect();
  };

  function forceComplete() {
    if (completed) return;
    cancelAnimationJob(queuedJob);
    finish?.();
    markComplete();
  }

  function start(velocity = 0) {
    if (started || completed) return;
    started = true;

    if (velocity >= fastVelocity || typeof play !== "function") {
      forceComplete();
      return;
    }

    queuedJob = {
      cancelled: false,
      release: null,
      run(releaseQueue) {
        if (completed || queuedJob.cancelled) {
          releaseQueue();
          return;
        }

        play(
          () => {
            markComplete();
            releaseQueue();
          },
          { velocity }
        );
      },
    };

    enqueueAnimation(queuedJob);
  }

  const evaluateIntersection = (intersectionEntry) => {
    if (completed) return;

    const rect = intersectionEntry.boundingClientRect;
    const viewportHeight =
      window.innerHeight || document.documentElement.clientHeight || 1;

    entry.top = rect.top + (window.scrollY || 0);
    entry.bottom = entry.top + Math.max(1, rect.height);

    if (rect.bottom <= 0) {
      forceComplete();
      return;
    }

    if (
      intersectionEntry.isIntersecting ||
      (rect.top <= viewportHeight + preload && rect.bottom >= -80)
    ) {
      start(currentVelocity);
    }
  };

  observer = new IntersectionObserver(
    (entries) => entries.forEach(evaluateIntersection),
    { rootMargin, threshold }
  );
  observer.observe(target);

  if ("ResizeObserver" in window) {
    resizeObserver = new ResizeObserver(() => {
      refreshGeometry(entry);
      scheduleGlobalCheck();
    });
    resizeObserver.observe(target);
  }

  registerEntry(entry);

  document.fonts?.ready?.then(() => {
    if (!completed) {
      refreshGeometry(entry);
      scheduleGlobalCheck();
    }
  });

  return {
    complete: forceComplete,
    destroy() {
      if (completed) return;
      cancelAnimationJob(queuedJob);
      completed = true;
      disconnect();
    },
  };
}

export default createOneTimeViewportAnimation;
